// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Listbox, Switch, Transition} from '@headlessui/react';
import {Image} from '@shopify/hydrogen';
import clsx from 'clsx';
import {Fragment} from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
} from 'react-hook-form';

import {ChevronDownIcon} from '~/components/icons';

export interface TField<T extends FieldValues> {
  ref: Path<T>;
  title?: string;
  required?: boolean;
  description?: string;
  children?: string;
  type: string;
  choices?: string[];
}

interface FormItemProps<T extends FieldValues> {
  field: TField<T>;
  multiple?: boolean;
  control: Control<T, any>;
  register: UseFormRegister<T>;
  isInvalid: boolean;
}

export function FormItem<T extends FieldValues>({
  field,
  multiple = false,
  control,
  register,
  isInvalid,
}: FormItemProps<T>) {
  const borderStyles = {
    'border-black focus:border-black': !isInvalid,
    'border-red-500': isInvalid,
  };

  switch (field.type) {
    case 'choices':
      return (
        <Controller
          defaultValue={multiple ? ([] as PathValue<T, Path<T>>) : undefined}
          name={field.ref}
          control={control}
          render={({field: renderField}) => {
            const selectedChoice = field?.choices?.find(
              (e: string) => e == renderField.value,
            );
            return (
              <Listbox {...renderField} multiple={multiple}>
                {({open}) => (
                  <>
                    <Listbox.Label className="block text-sm font-medium text-tundora-800">
                      {field?.title} {field.required && '*'}
                    </Listbox.Label>
                    <div className="relative mt-1">
                      <Listbox.Button
                        className={clsx(
                          'relative w-full cursor-pointer border bg-white py-4 pl-3 pr-10 text-left shadow-sm focus:outline-none sm:text-sm',
                          borderStyles,
                        )}
                      >
                        <span className="block truncate">
                          {multiple && renderField.value?.length > 0
                            ? renderField.value.map((choice: string) => (
                                <span
                                  className="mr-1 inline-flex items-center gap-1 rounded-[30px] bg-clay-100 px-2 py-1 text-acai-900"
                                  key={choice}
                                >
                                  <span>{choice}</span>
                                  <Image
                                    className="hover:bg-acai-300"
                                    src="/close.svg"
                                    height={12}
                                    width={12}
                                    alt="cross icon"
                                    onClick={() => {
                                      renderField.onChange(
                                        renderField.value.filter(
                                          (value: string) => value !== choice,
                                        ),
                                      );
                                    }}
                                  />
                                  {/* <button
                              className="bg-[red] px-2"
                              type="button"
                              onClick={() => {
                                const newArray = fieldValue.filter(
                                  (value: string) => value !== choice,
                                );
                                setFieldValue(newArray);
                                handleChange(newArray);
                              }}
                            >
                              X
                            </button> */}
                                </span>
                              ))
                            : selectedChoice ?? 'Please select'}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                          <ChevronDownIcon fill="black" />
                        </span>
                      </Listbox.Button>

                      <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {field?.choices?.map((e: string) => (
                            <Listbox.Option
                              key={e}
                              className={({active}) =>
                                clsx(
                                  active
                                    ? 'bg-acai-900 text-white'
                                    : 'text-gray-900',
                                  'relative mb-1 cursor-pointer select-none',
                                )
                              }
                              value={e}
                            >
                              {({selected}) => (
                                <>
                                  <span
                                    className={clsx(
                                      selected
                                        ? 'bg-acai-900 text-white'
                                        : 'font-normal',
                                      'block truncate py-2 pl-3 pr-9',
                                    )}
                                  >
                                    {e}
                                  </span>
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                    {field?.description && (
                      <div className="text-xs text-[#676767]">
                        {field?.description}
                      </div>
                    )}
                  </>
                )}
              </Listbox>
            );
          }}
        />
      );
    case 'toggle':
      return (
        <Controller
          {...register(field.ref)}
          control={control}
          render={({field: renderField}) => (
            <Switch.Group as="div" className="flex w-full items-center">
              <Switch
                {...renderField}
                className={clsx(
                  renderField.value ? 'bg-acai-900' : 'bg-white',
                  'relative inline-flex h-6 w-12 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent ring-2 ring-acai-900 ring-offset-2 transition-colors duration-200 ease-in-out focus:outline-none',
                )}
                checked={renderField.value}
                refName={field.ref}
                id={field.ref}
              >
                {renderField.value ? (
                  <span className="absolute translate-x-[2px] text-[11px] leading-[150%] text-white">
                    Yes
                  </span>
                ) : (
                  <span className="absolute translate-x-6 text-[11px] leading-[150%] text-acai-900">
                    No
                  </span>
                )}
                <span
                  aria-hidden="true"
                  className={clsx(
                    renderField.value
                      ? 'translate-x-6 bg-white'
                      : 'translate-x-0 bg-acai-900',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full shadow ring-0 transition duration-200 ease-in-out',
                  )}
                />
              </Switch>
              <Switch.Label as="span" className="ml-3">
                <span className="text-sm font-medium text-gray-900">
                  {field?.title && field?.title}
                  {field?.children && (
                    <div dangerouslySetInnerHTML={{__html: field?.children}} />
                  )}
                </span>
              </Switch.Label>
            </Switch.Group>
          )}
        />
      );
    case 'checkbox':
      return (
        <div className="flex">
          <input
            className="disabled:border-steel-400 disabled:bg-steel-400 peer relative mt-1 h-4 w-4 shrink-0 appearance-none rounded-sm border-2
        border-acai-900 bg-white checked:bg-white
        focus:outline-none focus:ring-1
        focus:ring-blue-100 focus:ring-offset-0"
            {...register(field.ref)}
            type="checkbox"
            id={field.ref}
            required={field.required ? true : false}
          />
          <svg
            className="pointer-events-none absolute mt-1 hidden h-4 w-4 stroke-acai-900 outline-none peer-checked:block"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <label className="ml-2 text-acai-900" htmlFor={field.ref}>
            {field?.title}
            {field?.children && (
              <div dangerouslySetInnerHTML={{__html: field?.children}} />
            )}
          </label>
        </div>
      );
    case 'textarea':
      return (
        <div className="flex w-full flex-col space-y-2">
          <div className="flex w-full text-tundora-800">
            <span className="text-sm">
              {field?.title} {field.required && '*'}
            </span>
          </div>
          <textarea
            {...register(field.ref)}
            className="relative w-full border-[1px] border-black bg-white py-4 pl-3 pr-10 text-left text-[#676767] shadow-sm placeholder:text-[#676767] focus:border-black focus:outline-none sm:text-sm"
            name={field.ref}
            placeholder={field?.title}
            required={field.required ? true : false}
            rows={5}
          />
          {field?.description && (
            <div className="text-xs text-[#676767]">{field?.description}</div>
          )}
        </div>
      );
    default:
      return (
        <div className="flex w-full flex-col space-y-2">
          <div className="flex w-full text-tundora-800">
            <span className="text-sm">
              {field?.title} {field.required && '*'}
            </span>
          </div>
          <input
            {...register(field.ref, {required: field.required})}
            className={clsx(
              'relative w-full border bg-white py-4 pl-3 pr-10 text-left text-[#676767] shadow-sm  placeholder:text-[#676767] focus:outline-none sm:text-sm',
              borderStyles,
            )}
            type={field.type}
            name={field.ref}
            placeholder={field?.title}
            required={field.required ? true : false}
          />
          {field?.description && (
            <div className="text-xs text-[#676767]">{field?.description}</div>
          )}
        </div>
      );
  }
}
